import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowRight, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { api, money } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Newsletter from "@/components/Newsletter";
import SizeGuide from "@/components/SizeGuide";
import Countdown from "@/components/Countdown";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FALLBACK = {
  slug: "classic-dejon-richh-tee",
  name: "Classic Dejon Richh Tee",
  price: 25.0,
  status: "live",
  sizes: ["S", "M", "L", "XL", "2XL"],
  images: ["/products/front.png", "/products/back.png", "/products/detail.png", "/products/collar.png"],
  description: "Classic Dejon Richh Tee — a premium everyday essential featuring the signature DR chest mark and Dejon Richh cross artwork on the back. Designed with a relaxed streetwear fit and a vintage-inspired finish.",
  details: ["Premium cotton", "Relaxed fit", "Vintage-inspired wash", "Front DR detail", "Signature Dejon Richh back artwork", "Unisex"],
};

const VIEW_LABELS = ["FRONT", "BACK", "DETAIL", "LABEL"];

function WaitlistForm({ slug }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("ENTER A VALID EMAIL");
      return;
    }
    setBusy(true);
    try {
      await api.joinWaitlist(email, slug);
      setDone(true);
    } catch {
      toast.error("SOMETHING WENT WRONG. TRY AGAIN.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <p className="mt-6 border border-wire px-5 py-4 font-mono text-[10px] tracking-[0.25em] text-neutral-300" data-testid="waitlist-success">
        YOU'RE ON THE WAITLIST. IF IT RETURNS, YOU'LL KNOW FIRST.
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="mt-6 flex flex-col gap-2 sm:flex-row" data-testid="waitlist-form">
      <input
        data-testid="waitlist-email-input"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="EMAIL FOR THE WAITLIST"
        className="flex-1 border border-wire bg-transparent px-4 py-3 font-mono text-[11px] tracking-[0.2em] text-white placeholder:text-neutral-600 focus:border-white focus:outline-none"
      />
      <button data-testid="waitlist-join-button" disabled={busy}
        className="bg-white px-6 py-3 font-mono text-[10px] tracking-[0.3em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-60">
        {busy ? "JOINING..." : "NOTIFY ME"}
      </button>
    </form>
  );
}

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, setDrawerOpen } = useCart();
  const [product, setProduct] = useState(FALLBACK);
  const [notFound, setNotFound] = useState(false);
  const [view, setView] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setView(0);
    setSize(null);
    setQty(1);
    api.getProduct(slug)
      .then((p) => { setProduct(p); setNotFound(false); })
      .catch(() => {
        if (slug === FALLBACK.slug) setProduct(FALLBACK);
        else setNotFound(true);
      });
  }, [slug]);

  const isLive = product.status === "live";
  const viewLabel = (i) => VIEW_LABELS[i] || `VIEW ${i + 1}`;
  const sizeLeft = size && product.stock ? product.stock[size] ?? 0 : null;
  const maxQty = sizeLeft !== null ? Math.max(1, Math.min(10, sizeLeft)) : 10;

  const requireSize = () => {
    if (!size) {
      toast.error("SELECT A SIZE FIRST");
      return false;
    }
    if (sizeLeft !== null && sizeLeft <= 0) {
      toast.error(`SIZE ${size} IS SOLD OUT`);
      return false;
    }
    return true;
  };

  const onAdd = () => {
    if (!requireSize()) return;
    addItem(product, size, Math.min(qty, maxQty));
    toast.success("ADDED TO CART");
  };

  const onBuyNow = () => {
    if (!requireSize()) return;
    addItem(product, size, Math.min(qty, maxQty));
    setDrawerOpen(false);
    navigate("/checkout");
  };

  if (notFound) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-5 pt-24 text-center" data-testid="product-not-found">
        <p className="font-display text-3xl font-extrabold uppercase tracking-tight text-neutral-500">Product not found</p>
        <Link to="/shop" data-testid="product-not-found-shop"
          className="mt-8 border border-wire px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-neutral-300 hover:border-white hover:text-white">
          BACK TO SHOP
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="product-page">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 pt-28 sm:pt-36 pb-20">
        <p className="font-mono text-[10px] tracking-[0.3em] text-neutral-600" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-white">HOME</Link> / <Link to="/shop" className="hover:text-white">SHOP</Link> / <span className="text-neutral-400">{product.name.toUpperCase()}</span>
        </p>

        <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* GALLERY */}
          <div className="lg:col-span-7" data-testid="product-gallery">
            <div className="relative overflow-hidden border border-wire bg-charcoal">
              <AnimatePresence mode="wait">
                <motion.img
                  key={view}
                  src={product.images[view] || product.images[0]}
                  alt={`${product.name} — ${viewLabel(view)} view`}
                  data-testid="product-main-image"
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="aspect-square w-full object-cover"
                />
              </AnimatePresence>
              <span className="absolute left-4 top-4 border border-wire bg-obsidian/80 px-3 py-1 font-mono text-[9px] tracking-[0.35em] text-neutral-300 backdrop-blur">
                {viewLabel(view)} VIEW
              </span>
              {!isLive && (
                <span className="absolute right-4 top-4 border border-white/40 bg-obsidian/80 px-3 py-1 font-mono text-[9px] tracking-[0.35em] text-white backdrop-blur" data-testid="product-coming-soon-badge">
                  COMING SOON
                </span>
              )}
            </div>
            {product.images.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-4">
                {product.images.map((src, i) => (
                  <button
                    key={src + i}
                    data-testid={`product-thumb-${viewLabel(i).toLowerCase().replace(" ", "-")}`}
                    onClick={() => setView(i)}
                    className={`overflow-hidden border bg-charcoal transition-colors ${view === i ? "border-white" : "border-wire hover:border-neutral-500"}`}
                  >
                    <img src={src} alt={`${viewLabel(i)} view thumbnail`} className="aspect-square w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* INFO */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <Reveal>
                <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">{(product.collection || "CLASSIC").toUpperCase()}</p>
                <h1 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white" data-testid="product-name">
                  {product.name}
                </h1>
                <p className="mt-3 font-script text-3xl text-neutral-100" data-testid="product-price">{money(product.price)}</p>

                {product.stock && (
                  <p className="mt-4 font-mono text-[10px] tracking-[0.3em] text-neutral-400" data-testid="stock-counter">
                    {isLive
                      ? `LIMITED RUN — ${product.stock_total} / ${product.run_size || product.stock_total} LEFT`
                      : `LIMITED RUN — ${product.run_size || product.stock_total} UNITS ONLY`}
                  </p>
                )}

                {product.description && (
                  <p className="mt-8 font-mono text-xs leading-relaxed tracking-wider text-neutral-400" data-testid="product-description">
                    {product.description}
                  </p>
                )}

                {isLive ? (product.members_only && !localStorage.getItem("dr_customer_token") ? (
                  <div className="mt-10 border border-wire p-8" data-testid="members-only-panel">
                    <p className="font-display text-xl font-extrabold uppercase tracking-tight text-white">Members Only</p>
                    <p className="mt-4 font-mono text-[11px] leading-relaxed tracking-wider text-neutral-400">
                      THIS DROP IS MEMBERS-FIRST FOR 24 HOURS. SIGN IN OR CREATE A FREE ACCOUNT TO BUY IT RIGHT NOW.
                      {product.members_ends && ` OPENS TO EVERYONE ${new Date(product.members_ends).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).toUpperCase()}.`}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <Link to="/login" data-testid="members-login-link"
                        className="bg-white px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-black transition-colors hover:bg-neutral-300">
                        SIGN IN
                      </Link>
                      <Link to="/signup" data-testid="members-signup-link"
                        className="border border-wire px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-neutral-300 transition-colors hover:border-white hover:text-white">
                        CREATE ACCOUNT
                      </Link>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mt-10">
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">SIZE</p>
                        <div className="flex items-center gap-4">
                          <p className="hidden sm:block font-mono text-[10px] tracking-[0.2em] text-neutral-600">RELAXED FIT — TRUE TO SIZE</p>
                          <SizeGuide />
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2" data-testid="size-selector">
                        {product.sizes.map((s) => {
                          const left = product.stock ? product.stock[s] ?? 0 : null;
                          const gone = left !== null && left <= 0;
                          return (
                            <button
                              key={s}
                              data-testid={`size-option-${s.toLowerCase()}`}
                              disabled={gone}
                              onClick={() => { setSize(s); setQty(1); }}
                              className={`min-w-[3.5rem] border px-4 py-3.5 font-mono text-xs tracking-[0.2em] transition-colors ${
                                gone
                                  ? "cursor-not-allowed border-wire text-neutral-600 line-through"
                                  : size === s
                                    ? "border-white bg-white text-black"
                                    : "border-wire text-neutral-300 hover:border-neutral-400"
                              }`}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>
                      {sizeLeft !== null && sizeLeft > 0 && sizeLeft <= 3 && (
                        <p className="mt-3 font-mono text-[10px] tracking-[0.25em] text-white" data-testid="low-stock-hint">
                          ONLY {sizeLeft} LEFT IN SIZE {size}
                        </p>
                      )}
                    </div>

                    <div className="mt-8">
                      <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">QUANTITY</p>
                      <div className="mt-4 inline-flex items-center border border-wire" data-testid="quantity-selector">
                        <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-neutral-400 hover:text-white" aria-label="Decrease quantity">
                          <Minus size={14} />
                        </button>
                        <span data-testid="qty-value" className="w-12 text-center font-mono text-sm text-white">{qty}</span>
                        <button data-testid="qty-plus" onClick={() => setQty(Math.min(maxQty, qty + 1))} className="px-4 py-3 text-neutral-400 hover:text-white" aria-label="Increase quantity">
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <button
                        data-testid="add-to-cart-button"
                        onClick={onAdd}
                        className="border border-white py-4 font-mono text-[11px] tracking-[0.35em] text-white transition-colors hover:bg-white hover:text-black"
                      >
                        ADD TO CART
                      </button>
                      <button
                        data-testid="buy-now-button"
                        onClick={onBuyNow}
                        className="group flex items-center justify-center gap-3 bg-white py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300"
                      >
                        BUY NOW <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                      </button>
                    </div>

                    <p className="mt-6 font-mono text-[10px] tracking-[0.3em] text-neutral-600">SHIPS WORLDWIDE — EST. 2026</p>
                  </>
                )) : product.status === "sold_out" ? (
                  <div className="mt-10 border border-wire p-8" data-testid="product-sold-out-panel">
                    <p className="font-display text-xl font-extrabold uppercase tracking-tight text-white">Sold Out</p>
                    <p className="mt-4 font-mono text-[11px] leading-relaxed tracking-wider text-neutral-400">
                      THIS RUN IS GONE. LIMITED DROPS DON'T RESTOCK — BUT IF IT EVER RETURNS, THE WAITLIST HEARS FIRST.
                    </p>
                    <WaitlistForm slug={product.slug} />
                    <Link to="/shop" data-testid="sold-out-back-to-shop"
                      className="mt-6 inline-block border border-wire px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-neutral-300 transition-colors hover:border-white hover:text-white">
                      SHOP WHAT'S LIVE
                    </Link>
                  </div>
                ) : (
                  <div className="mt-10 border border-wire p-8" data-testid="product-coming-soon-panel">
                    <p className="font-display text-xl font-extrabold uppercase tracking-tight text-white">Coming Soon</p>
                    <p className="mt-4 font-mono text-[11px] leading-relaxed tracking-wider text-neutral-400">
                      THIS PIECE IS STILL IN THE WORKS. SET A DROP DATE IN THE STUDIO AND THE COUNTDOWN RUNS ITSELF — WHEN IT HITS ZERO, THE PRODUCT GOES LIVE AUTOMATICALLY.
                    </p>
                    {product.drop_date && <Countdown target={product.drop_date} />}
                    <WaitlistForm slug={product.slug} />
                    <Link to="/shop" data-testid="coming-soon-back-to-shop"
                      className="mt-6 inline-block border border-wire px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-neutral-300 transition-colors hover:border-white hover:text-white">
                      SHOP WHAT'S LIVE
                    </Link>
                  </div>
                )}

                <Accordion type="single" collapsible className="mt-10 border-t border-wire">
                  <AccordionItem value="details" className="border-wire">
                    <AccordionTrigger data-testid="accordion-details" className="font-mono text-[11px] tracking-[0.3em] text-neutral-300 hover:text-white hover:no-underline">
                      PRODUCT DETAILS
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 pb-2">
                        {(product.details || []).map((d) => (
                          <li key={d} className="font-mono text-[11px] tracking-[0.2em] text-neutral-400">
                            <span className="text-neutral-600">/&nbsp;&nbsp;</span>{d.toUpperCase()}
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="shipping" className="border-wire">
                    <AccordionTrigger data-testid="accordion-shipping" className="font-mono text-[11px] tracking-[0.3em] text-neutral-300 hover:text-white hover:no-underline">
                      SHIPPING & RETURNS
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="font-mono text-[11px] leading-relaxed tracking-wider text-neutral-400">
                        Standard shipping 5–7 business days, express 2–3 business days. Worldwide delivery. Returns accepted within 30 days of delivery — item must be unworn with tags attached. See{" "}
                        <Link to="/policies/returns" className="underline underline-offset-4 hover:text-white">Returns</Link> for details.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
      {isLive ? (
        <Marquee items={["PREMIUM COTTON", "RELAXED FIT", "VINTAGE WASH", "SIGNATURE ARTWORK", "UNISEX"]} />
      ) : (
        <Newsletter />
      )}
    </div>
  );
}
